import { buyerNavConfig } from "@/config/navigation/buyerNav";
import { cn } from "@/lib/utils";
import { NavSection } from "@/components/navigation/NavSection";
import { NavItem } from "@/components/navigation/NavItem";
import { useNavigate } from "react-router-dom";
import { useSidebar } from "@/contexts/SidebarContext";
import { ChevronLeft, ChevronRight, Settings } from "lucide-react";

interface BuyerSidebarProps {
  variant?: "desktop" | "drawer";
  onNavigate?: () => void;
}

export function BuyerSidebar({ variant = "desktop", onNavigate }: BuyerSidebarProps) {
  const navigate = useNavigate();
  const { sections } = buyerNavConfig;
  const { isCollapsed, toggleSidebar } = useSidebar();
  // Mobile drawer: always show full labels (icons + text) for accessibility
  const effectiveCollapsed = variant === "drawer" ? false : isCollapsed;

  const handleLogoClick = () => {
    navigate("/buyer/dashboard");
    onNavigate?.();
  };

  return (
    <nav
      className={cn(
        "flex h-full flex-col overflow-hidden w-full",
        "bg-muted/50 border-r border-border shadow-sm",
        "transition-[width] duration-200 ease-out"
      )}
      aria-label="Primary navigation"
    >
      {/* Sticky header: logo + collapse */}
      <header
        className={cn(
          "flex-shrink-0 border-b border-border bg-white",
          effectiveCollapsed ? "px-0 py-3" : "px-3 py-3"
        )}
      >
        <div className={cn("flex items-center", effectiveCollapsed ? "justify-center gap-0" : "justify-between gap-2")}>
          {!effectiveCollapsed ? (
            <>
              <button
                type="button"
                onClick={handleLogoClick}
                className="flex min-w-0 flex-1 cursor-pointer items-center gap-4 rounded-lg text-left transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-white"
                title="Go to Dashboard"
                aria-label="Go to Dashboard"
              >
                <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary text-sm font-bold text-white">
                  HS
                </div>
                <div className="flex min-w-0 flex-col items-start justify-center gap-0.5">
                  <span className="truncate text-lg font-bold uppercase leading-tight tracking-tight text-foreground">HoleScale.</span>
                  <span className="truncate text-xs leading-tight text-muted-foreground">Buyer workspace</span>
                </div>
              </button>
              <button
                type="button"
                onClick={toggleSidebar}
                className="min-h-[44px] min-w-[44px] shrink-0 rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-white"
                aria-label="Collapse sidebar"
              >
                <ChevronLeft className="size-5" />
              </button>
            </>
          ) : (
            <div className="flex w-full flex-col items-center gap-4">
              <button
                type="button"
                onClick={toggleSidebar}
                className="flex min-h-[44px] w-full min-w-[44px] justify-center rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                aria-label="Expand sidebar"
              >
                <ChevronRight className="size-5" />
              </button>
              <button
                type="button"
                onClick={handleLogoClick}
                className="rounded-lg p-0 transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                title="Go to Dashboard"
                aria-label="Go to Dashboard"
              >
                <div className="flex size-8 items-center justify-center rounded-lg bg-primary text-sm font-bold text-white">
                  HS
                </div>
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Scrollable nav with fade indicators */}
      <div className="relative flex min-h-0 flex-1 flex-col">
        <div
          className="pointer-events-none absolute inset-x-0 top-0 z-10 h-4 bg-gradient-to-b from-muted to-transparent opacity-60"
          aria-hidden
        />
        <div
          className={cn(
            "flex-1 overflow-y-auto overflow-x-hidden sidebar-scroll",
            "transition-all duration-200",
            effectiveCollapsed ? "py-2 px-2 pb-2" : "py-3 px-3 pb-4 space-y-0.5"
          )}
        >
          {!effectiveCollapsed ? (
            sections.map((section) => (
              <NavSection
                key={section.id}
                section={section}
                onNavigate={onNavigate}
                collapsed={false}
                isFirstSection={section.id === "home"}
              />
            ))
          ) : (
            <div className="flex w-full flex-col items-center gap-1">
              {sections.flatMap((section) =>
                section.items.map((item) => (
                  <li key={item.href} className="flex w-full list-none justify-center">
                    <NavItem
                      item={item}
                      onNavigate={onNavigate}
                      collapsed={true}
                      isInQuickActions={false}
                    />
                  </li>
                ))
              )}
            </div>
          )}
        </div>
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-4 bg-gradient-to-t from-muted to-transparent opacity-60"
          aria-hidden
        />
      </div>

      {/* Sticky footer: settings only (user identity is in top nav) */}
      <footer
        className={cn(
          "flex-shrink-0 border-t border-border bg-white/80 backdrop-blur-sm",
          "transition-all duration-200",
          effectiveCollapsed ? "p-2 flex justify-center items-center" : "py-3 px-3"
        )}
      >
        <a
          href="/buyer/settings"
          onClick={(e) => {
            e.preventDefault();
            navigate("/buyer/settings");
            onNavigate?.();
          }}
          className={cn(
            "flex items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
            effectiveCollapsed ? "w-11 h-11 p-0 min-h-[44px] min-w-[44px]" : "p-2 gap-2 w-full justify-start"
          )}
          aria-label="Settings"
        >
          <Settings className="size-5 shrink-0" />
          {!effectiveCollapsed && <span className="text-sm font-medium">Settings</span>}
        </a>
      </footer>
    </nav>
  );
}

